import { useEffect, useRef, useState } from "react";

import type { C_ProductInfo, ProductItem } from "~/lib/app.types";

import { useBasket, useBasketActions } from "../hooks/basket-hooks";
import {
  BasketLineItemContext,
  type BasketLineItemSurface,
  useBasketHandle,
} from "./basket-line-item.hooks";

export const BasketLineItemRoot = ({
  children,
  boundryRef,
  lineItem,
  productItems,
  scope = "cart",
  rowIndex,
}: {
  children: React.ReactNode | ((isStale: boolean) => React.ReactNode);
  boundryRef: React.RefObject<HTMLElement>;
  lineItem: ProductItem;
  productItems?: C_ProductInfo[];
  /** Surface this row renders in; defaults to the cart page. Drives the
   *  test-id scope (`<scope>-item-*`) so locators don't match across surfaces. */
  scope?: BasketLineItemSurface;
  rowIndex?: number;
}) => {
  const handle = useBasketHandle();
  const basket = useBasket();
  const products = productItems || basket?.products || {};
  const itemId = lineItem.itemId;
  const { fetcher } = useBasketActions();
  const [isStale, setIsStale] = useState(false);
  // COM-63 (P1-1 B/C): mirror the local remove's in-flight state instead of
  // latching `true` — a contained failure keeps the row, and the latch left it
  // dimmed until remount. Transition-guarded (see `wasRemovingRef` in the
  // remove button) because `setIsStale` is shared with the other row actions.
  const isLocalRemoving =
    fetcher.state !== "idle" && fetcher.formData?.get("action") === "remove";
  const wasLocalRemovingRef = useRef(false);
  useEffect(() => {
    if (isLocalRemoving) {
      wasLocalRemovingRef.current = true;
      setIsStale(true);
    } else if (wasLocalRemovingRef.current) {
      wasLocalRemovingRef.current = false;
      setIsStale(false);
    }
  }, [isLocalRemoving, itemId, lineItem.quantity, setIsStale]);

  if (!itemId) return null;
  if (!products) return null;
  const idScope = `${scope}-item`;
  return (
    <BasketLineItemContext.Provider
      value={{
        handle,
        boundryRef,
        lineItem,
        isStale,
        setIsStale,
        idScope,
        rowIndex,
      }}
    >
      {typeof children === "function" ? children(isStale) : children}
    </BasketLineItemContext.Provider>
  );
};
BasketLineItemRoot.displayName = "BasketLineItem.Root";
